import React, { createContext, useState, useEffect, useCallback, ReactNode } from 'react';
import apiClient from '../api/client';
import { useAuth } from '../hooks/useAuth';

/**
 * Branch interface matching backend Branch schema
 */
export interface Branch {
  _id: string;
  name: string;
  code: string;
  address?: string;
  city?: string;
  phone?: string;
  isActive: boolean;
}

/**
 * Branch context state interface
 */
export interface BranchContextType {
  branches: Branch[];
  activeBranch: Branch | null;
  isLoading: boolean;
  error: string | null;
  switchBranch: (branchId: string) => Promise<void>;
  refreshBranches: () => Promise<void>;
  clearError: () => void;
}

/**
 * Branch Context
 * Provides branch state and branch switching functionality throughout the application
 * Validates: Requirements 18.6
 */
export const BranchContext = createContext<BranchContextType | undefined>(undefined);

interface BranchProviderProps {
  children: ReactNode;
}

/**
 * Branch Provider Component
 * Manages branch state, branch switching, and branch list
 */
const BranchProvider: React.FC<BranchProviderProps> = ({ children }) => {
  const { user, isAuthenticated, updateUser } = useAuth();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [activeBranch, setActiveBranch] = useState<Branch | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch all branches from backend
   */
  const fetchBranches = useCallback(async () => {
    if (!isAuthenticated) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await apiClient.get('/branches');
      const allBranches = response.data;
      setBranches(allBranches);

      // Set active branch based on user's activeBranch
      if (user?.activeBranch) {
        const active = allBranches.find((b: Branch) => b._id === user.activeBranch);
        setActiveBranch(active || null);
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Şubeler yüklenirken hata oluştu';
      setError(errorMessage);
      console.error('Failed to fetch branches:', err);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated]);

  /**
   * Initialize branches on component mount or when authentication status changes
   * Only fetches branches once when user logs in, not on every user update
   */
  useEffect(() => {
    if (isAuthenticated && user && branches.length === 0) {
      // Only fetch if we don't have branches yet
      fetchBranches();
    } else if (!isAuthenticated) {
      // Clear state when user logs out
      setBranches([]);
      setActiveBranch(null);
    }
  }, [isAuthenticated, user, fetchBranches, branches.length]);

  /**
   * Update active branch when user's activeBranch changes
   * This handles branch switching without refetching all branches
   */
  useEffect(() => {
    if (user?.activeBranch && branches.length > 0) {
      const active = branches.find((b: Branch) => b._id === user.activeBranch);
      if (active && active._id !== activeBranch?._id) {
        setActiveBranch(active);
      }
    }
  }, [user?.activeBranch, branches, activeBranch?._id]);

  /**
   * Switch user's active branch
   * Calls backend API to update user's activeBranch
   * Updates auth context's user.activeBranch after successful switch
   * @param branchId - ID of the branch to switch to
   */
  const switchBranch = async (branchId: string): Promise<void> => {
    if (!user) {
      throw new Error('Kullanıcı oturumu bulunamadı');
    }

    // Validate that the branch exists in user's branches
    if (!user.branches.includes(branchId)) {
      throw new Error('Bu şubeye geçiş yetkiniz bulunmamaktadır');
    }

    setIsLoading(true);
    setError(null);

    try {
      // Call backend API to switch branch
      await apiClient.put(`/users/${user.id}/switch-branch`, {
        branchId,
      });

      // Update active branch in local state
      const newActiveBranch = branches.find((b) => b._id === branchId);
      setActiveBranch(newActiveBranch || null);

      // Update user in auth context to reflect the new activeBranch
      await updateUser();

      setError(null);
    } catch (err: any) {
      const errorMessage = err.message || 'Şube değiştirme başarısız oldu';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Refresh branches list
   */
  const refreshBranches = async (): Promise<void> => {
    await fetchBranches();
  };

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const value: BranchContextType = {
    branches,
    activeBranch,
    isLoading,
    error,
    switchBranch,
    refreshBranches,
    clearError,
  };

  return <BranchContext.Provider value={value}>{children}</BranchContext.Provider>;
};

export default BranchProvider;
